"use client";
import { motion } from "framer-motion";
import Image from "next/image";
import { SectionHeading, Caption } from "@/components/ui/editorial";

export default function TodoLoQueNoVesFundamentacion() {
	return (
		<section
			id="fundamentacion"
			className="px-4 xl:px-0 py-16 border-t border-border"
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<motion.header
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					viewport={{ once: true }}
					className="mb-16"
				>
					<SectionHeading
						index="01"
						title="Fundamentación del Proyecto"
						kicker="Fundamentación"
					/>
					<p className="font-body text-lg mt-6 max-w-4xl leading-relaxed text-muted-foreground">
						La base teórica y social que sustenta nuestras acciones para la
						transformación de conciencias y la construcción de vínculos más sanos
						y afectivos.
					</p>
				</motion.header>

				{/* Contenido Principal */}
				<div className="grid lg:grid-cols-2 gap-16 items-start">
					{/* Panel Izquierdo - Texto */}
					<motion.div
						initial={{ opacity: 0, x: -20 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
						className="space-y-8"
					>
						<p className="font-body text-lg text-ink/80 leading-relaxed border-l-2 border-border pl-6">
							La violencia intrafamiliar, el abuso y la negligencia educativa son
							realidades que afectan profundamente el desarrollo emocional y
							psicológico de niños, niñas y adolescentes. Estas experiencias
							traumáticas, a menudo invisibilizadas por el mito romántico de la
							familia, dejan heridas que perduran en la edad adulta y se
							transmiten generacionalmente.
						</p>

						<p className="font-body text-lg text-ink/80 leading-relaxed border-l-2 border-border pl-6">
							La educación emocional y el cuidado de la infancia son pilares
							fundamentales para romper estos ciclos de violencia. Es necesario
							crear espacios de conciencia que permitan reconocer, validar y
							procesar estas experiencias, facilitando procesos de sanación y
							reparación que promuevan vínculos más saludables.
						</p>

						<p className="font-body text-lg text-ink/80 leading-relaxed border-l-2 border-border pl-6">
							El arte, la literatura y la expresión creativa se convierten en
							herramientas poderosas para abordar estas realidades complejas. A
							través de la narrativa, la danza y la reflexión colectiva, podemos
							crear puentes de comprensión y empatía que faciliten la
							transformación personal y social.
						</p>

						<p className="font-body text-lg text-ink/80 leading-relaxed border-l-2 border-border pl-6">
							La comunidad tiene un papel esencial en este proceso de
							transformación. Al crear redes de apoyo, espacios de encuentro y
							herramientas de autocuidado, podemos construir una sociedad más
							compasiva, consciente y resiliente que proteja y nutra el bienestar
							emocional de todas las personas.
						</p>
					</motion.div>

					{/* Panel Derecho - Imagen + Cita */}
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						viewport={{ once: true }}
						className="lg:sticky lg:top-8 space-y-8"
					>
						<figure>
							<div className="relative w-full h-96 rounded-xl border border-border overflow-hidden">
								<Image
									src="/assets/todo-lo-que-no-ves/fundamentacion.jpeg"
									alt="Fundamentación del proyecto Todo lo que no ves"
									fill
									sizes="(max-width: 1024px) 100vw, 50vw"
									className="object-cover"
								/>
							</div>
							<Caption>Fundamentación del proyecto</Caption>
						</figure>

						{/* Pull-quote */}
						<blockquote className="border-l-2 border-terracotta pl-6 py-2">
							<p className="font-display italic text-terracotta text-xl leading-relaxed">
								&ldquo;La transformación social comienza con la transformación
								individual. Cada persona que reconoce sus heridas y decide
								sanarlas, contribuye a crear un mundo más compasivo y
								consciente.&rdquo;
							</p>
							<footer className="font-body text-sm text-muted-foreground mt-3">
								— Aránzazu G. Butler
							</footer>
						</blockquote>
					</motion.div>
				</div>

				{/* Tarjetas de apoyo */}
				<div className="grid md:grid-cols-3 gap-6 mt-16 pt-16 border-t border-border">
					{[
						{
							titulo: "Cuidado Emocional",
							texto: "Espacios seguros para el procesamiento emocional",
						},
						{
							titulo: "Educación Emocional",
							texto: "Herramientas para el desarrollo emocional saludable",
						},
						{
							titulo: "Vínculos Sanos",
							texto: "Construcción de relaciones afectivas saludables",
						},
					].map((card, i) => (
						<motion.div
							key={card.titulo}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: i * 0.2 }}
							viewport={{ once: true }}
							className="border border-border rounded-xl p-6"
						>
							<h3 className="font-display text-lg font-semibold text-ink mb-2">
								{card.titulo}
							</h3>
							<p className="font-body text-sm text-muted-foreground">
								{card.texto}
							</p>
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
}
